import React, { useState, useEffect, useMemo } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Autocomplete,
  Checkbox,
  ListItemText,
  Tooltip,
  IconButton,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  TablePagination,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  FileDownload,
  Refresh,
  Clear,
  Warehouse as WarehouseIcon,
  Close,
  HelpOutline,
} from '@mui/icons-material';
import { API_BASE_URL } from '../apiClient';

// ─── Types ───────────────────────────────────────────────────────────────────
interface OutageRecord {
  date: string;
  end_time: string;
  id: number;
  inc_id: string;
  name: string;
  outage_duration_secs: number;
  outage_number: string;
  start_time: string;
  warehouse_city: string;
  warehouse_country: string;
  warehouse_region: string;
}

interface OutageResponse {
  count: number;
  data: OutageRecord[];
}

interface WarehouseMetadata {
  name: string;
  site_code: string;
  country: string;
  location_status: string;
  region_name: string;
  is_critical: boolean | null;
}

interface WarehouseResponse {
  status: string;
  data: WarehouseMetadata[];
}

interface MetricsByPeriod {
  [period: string]: {
    incidentCount: number;
    totalOutageSeconds: number;
    totalAvailableSeconds: number;
    previousYearIncidentCount?: number; // For P1P2 target calculation
  };
}

interface HierarchyNode {
  name: string;
  level: 'region' | 'country' | 'warehouse';
  metrics: MetricsByPeriod;
  records: OutageRecord[];  // Store records for accurate totals calculation
  children?: HierarchyNode[];
}

const SELECT_ALL_OPTION = 'Select All';

// ─── Region Name Transformation ──────────────────────────────────────────────
// Maps full region names to abbreviated codes
const REGION_NAME_TRANSFORM: Record<string, string> = {
  'Asia Pacific': 'APA',
  'Middle East and Africa': 'IMEA',
  'North America': 'NAM',
  'Latin America': 'LAM',
  'Europe': 'EUR',
  'West Central Asia': 'IMEA'
};

// ─── Region Override Mapping ─────────────────────────────────────────────────
// Maps specific countries to their proposed regions (using abbreviated names)
const COUNTRY_REGION_OVERRIDE: Record<string, string> = {
  'Bangladesh': 'IMEA',
  'India': 'IMEA',
  'Marshall Islands': 'APA',
  'Mexico': 'NAM',
  'Sao Tome and Principe': 'IMEA',
  'Sri Lanka': 'IMEA',
  'St Kitts-Nevis': 'LAM',
};

// ─── Country Name Standardization ──────────────────────────────────────────
const COUNTRY_NAME_STANDARDIZATION: Record<string, string> = {
  'USA': 'United States of America',
};

// ─── Helper Functions ────────────────────────────────────────────────────────
const formatLocalDate = (date: Date): string => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getISOWeekNumber = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
};

const getISOWeekYear = (date: Date): number => {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  return d.getUTCFullYear();
};

// ─── Component ───────────────────────────────────────────────────────────────
const WarehouseAvailability: React.FC = () => {
  // Core state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OutageRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [warehousesData, setWarehousesData] = useState<WarehouseMetadata[]>([]);

  // Period type
  const [periodType, setPeriodType] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('weekly');

  // Time Range state (similar to Portfolio Health Tracker)
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('1y');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });

  // Computed date range from selected time range
  const [filterStartDate, setFilterStartDate] = useState<string | undefined>();
  const [filterEndDate, setFilterEndDate] = useState<string | undefined>();

  // Location filters
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);
  const [selectedLocationStatus, setSelectedLocationStatus] = useState<string[]>([]);
  const [showRedOnly, setShowRedOnly] = useState(false);



  // View By
  const [viewBy, setViewBy] = useState<'Region' | 'Country' | 'Warehouse'>('Region');

  // Hierarchy expansion
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Active tab
  const [activeTab, setActiveTab] = useState<'availability' | 'p1p2'>('availability');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);

  // Dialog state for showing incident details
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogData, setDialogData] = useState<{
    locationName: string;
    period: string;
    records: OutageRecord[];
    totalAvailableSeconds?: number;
  } | null>(null);

  // ─── Fetch Data ────────────────────────────────────────────────────────────
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all endpoints in parallel
      const [outagesResponse, nonCriticalOutagesResponse, warehousesResponse] = await Promise.all([
        authFetch(`${API_BASE_URL}/api/operational-excellence/consolidated-outages`),
        authFetch(`${API_BASE_URL}/api/operational-excellence/non-critical-warehouse`),
        authFetch(`${process.env.REACT_APP_NOC_DASHBOARD_API}/warehouse-availability/warehouses`)
      ]);
      
      if (!outagesResponse.ok) throw new Error(`Outages API error! status: ${outagesResponse.status}`);
      if (!nonCriticalOutagesResponse.ok) throw new Error(`Non-critical outages API error! status: ${nonCriticalOutagesResponse.status}`);
      if (!warehousesResponse.ok) throw new Error(`Warehouses API error! status: ${warehousesResponse.status}`);
      
      const outagesResult: OutageResponse = await outagesResponse.json();
      const nonCriticalOutagesResult: OutageResponse = await nonCriticalOutagesResponse.json();
      const warehousesResult: WarehouseResponse = await warehousesResponse.json();
      
      // Merge both outage datasets
      const mergedOutagesData = [
        ...(outagesResult.data || []),
        ...(nonCriticalOutagesResult.data || [])
      ];
      
      // Apply region overrides and transformation to merged outage data
      const processedData = mergedOutagesData.map(record => {
        // Standardize country name
        const country = COUNTRY_NAME_STANDARDIZATION[record.warehouse_country] || record.warehouse_country;
        
        // First, apply country-specific region override if exists
        let regionName = COUNTRY_REGION_OVERRIDE[country] || record.warehouse_region;
        
        // If not already transformed (overrides are already abbreviated), transform the full name
        if (!COUNTRY_REGION_OVERRIDE[country]) {
          regionName = REGION_NAME_TRANSFORM[record.warehouse_region] || record.warehouse_region;
        }
        
        return {
          ...record,
          warehouse_country: country,
          warehouse_region: regionName
        };
      });
      
      // Apply region transformation to warehouse metadata
      const processedWarehouses = (warehousesResult.data || []).map(warehouse => {
        // Standardize country name
        const country = COUNTRY_NAME_STANDARDIZATION[warehouse.country] || warehouse.country;
        
        let regionName = COUNTRY_REGION_OVERRIDE[country] || warehouse.region_name;
        
        if (!COUNTRY_REGION_OVERRIDE[country]) {
          regionName = REGION_NAME_TRANSFORM[warehouse.region_name] || warehouse.region_name;
        }
        
        return {
          ...warehouse,
          country: country,
          region_name: regionName
        };
      });
      
      setData(processedData);
      setTotalCount((outagesResult.count || 0) + (nonCriticalOutagesResult.count || 0) || processedData.length || 0);
      setWarehousesData(processedWarehouses);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // ─── Time Range Effect (similar to Portfolio Health Tracker) ──────────────
  useEffect(() => {
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const endDate = formatLocalDate(todayUTC);
    let startDate = '';

    switch (selectedTimeRange) {
      case '7d': {
        const start = new Date(todayUTC);
        start.setUTCDate(start.getUTCDate() - 7);
        startDate = formatLocalDate(start);
        break;
      }
      case '30d': {
        const start = new Date(todayUTC);
        start.setUTCDate(start.getUTCDate() - 30);
        startDate = formatLocalDate(start);
        break;
      }
      case '90d': {
        const start = new Date(todayUTC);
        start.setUTCDate(start.getUTCDate() - 90);
        startDate = formatLocalDate(start);
        break;
      }
      case '1y': {
        // Year to Date
        const start = new Date(Date.UTC(todayUTC.getUTCFullYear(), 0, 1));
        startDate = formatLocalDate(start);
        break;
      }
      case 'all': {
        // Show all data without date filtering
        setFilterStartDate(undefined);
        setFilterEndDate(undefined);
        return;
      }
      case 'custom': {
        if (customDateRange.start && customDateRange.end) {
          setFilterStartDate(customDateRange.start);
          setFilterEndDate(customDateRange.end);
        }
        return;
      }
    }

    if (selectedTimeRange !== 'custom') {
      setFilterStartDate(startDate);
      setFilterEndDate(endDate);
    }
  }, [selectedTimeRange, customDateRange]);

  const clearAllFilters = () => {
    setPeriodType('weekly');
    setSelectedTimeRange('1y');
    setShowCustomDatePicker(false);
    setCustomDateRange({ start: '', end: '' });
    setSelectedRegions([]);
    setSelectedCountries([]);
    setSelectedWarehouses([]);
    setShowCriticalOnly(false);
    setSelectedLocationStatus([]);
    setShowRedOnly(false);
    setViewBy('Region');
    setExpandedNodes(new Set());
    setPage(0);
  };

  // ─── Derived: Min/Max dates from data ──────────────────────────────────────
  const dataDateRange = useMemo(() => {
    if (data.length === 0) {
      const today = new Date();
      const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      return { min: formatLocalDate(todayUTC), max: formatLocalDate(todayUTC) };
    }
    const dates = data.map(r => new Date(r.date).getTime());
    return {
      min: formatLocalDate(new Date(Math.min(...dates))),
      max: formatLocalDate(new Date())
    };
  }, [data]);

  // ─── Derived: Dynamic location filter options ──────────────────────────────
  const availableRegions = useMemo(() => {
    return Array.from(new Set(warehousesData.map(w => w.region_name))).sort();
  }, [warehousesData]);

  const availableCountries = useMemo(() => {
    let filtered = warehousesData;
    if (selectedRegions.length > 0) filtered = filtered.filter(w => selectedRegions.includes(w.region_name));
    return Array.from(new Set(filtered.map(w => w.country))).sort();
  }, [warehousesData, selectedRegions]);

  const availableWarehouses = useMemo(() => {
    let filtered = warehousesData;
    if (selectedRegions.length > 0) filtered = filtered.filter(w => selectedRegions.includes(w.region_name));
    if (selectedCountries.length > 0) filtered = filtered.filter(w => selectedCountries.includes(w.country));
    return Array.from(new Set(filtered.map(w => w.name))).sort();
  }, [warehousesData, selectedRegions, selectedCountries]);

  const availableLocationStatuses = useMemo(() => {
    return Array.from(new Set(warehousesData.map(w => w.location_status))).sort();
  }, [warehousesData]);

  // ─── Select All helper for Autocomplete ────────────────────────────────────
  const makeSelectAllAutocomplete = (
    label: string,
    options: string[],
    selected: string[],
    setSelected: (val: string[]) => void,
    onClearDownstream?: () => void,
  ) => {
    const allOptions = [SELECT_ALL_OPTION, ...options];
    const isAllSelected = options.length > 0 && selected.length === options.length;

    return (
      <Autocomplete
        multiple
        size="small"
        limitTags={1}
        options={allOptions}
        value={isAllSelected ? [SELECT_ALL_OPTION, ...selected] : selected}
        onChange={(_, newValue) => {
          const hasSelectAll = newValue.includes(SELECT_ALL_OPTION);
          const prevHasSelectAll = isAllSelected;
          if (hasSelectAll && !prevHasSelectAll) {
            setSelected([...options]);
          } else if (!hasSelectAll && prevHasSelectAll) {
            setSelected([]);
          } else {
            setSelected(newValue.filter(v => v !== SELECT_ALL_OPTION));
          }
          onClearDownstream?.();
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            sx={{
              '& .MuiInputBase-root': { fontSize: '0.8rem', minHeight: '36px', maxHeight: '36px' },
              '& .MuiInputLabel-root': { fontSize: '0.8rem' },
            }}
          />
        )}
        renderOption={(props, option, { selected: sel }) => {
          const isSelectAll = option === SELECT_ALL_OPTION;
          return (
            <li {...props} style={{ ...props.style, fontWeight: isSelectAll ? 600 : 'normal' }}>
              <Checkbox checked={isSelectAll ? isAllSelected : sel} size="small" />
              <ListItemText primary={option} sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }} />
            </li>
          );
        }}
        renderTags={(value, getTagProps) => {
          const filtered = value.filter(v => v !== SELECT_ALL_OPTION);
          if (isAllSelected) {
            return <Chip {...getTagProps({ index: 0 })} label="All" size="small" sx={{ fontSize: '0.7rem', height: 20 }} />;
          }
          if (filtered.length === 0) return null;
          return (
            <>
              <Chip {...getTagProps({ index: 0 })} label={filtered[0]} size="small" sx={{ fontSize: '0.7rem', height: 20, maxWidth: 80 }} />
              {filtered.length > 1 && (
                <Typography variant="caption" sx={{ ml: 0.5, fontSize: '0.7rem' }}>+{filtered.length - 1}</Typography>
              )}
            </>
          );
        }}
        sx={{ '& .MuiAutocomplete-inputRoot': { flexWrap: 'nowrap' } }}
      />
    );
  };

  // ─── Filter data ───────────────────────────────────────────────────────────
  const { filteredData, globalRecords } = useMemo(() => {
    let filtered = data;

    // The report scope floor is 2025-W1; respect the active filter start date when set
    const chartStart = filterStartDate
      ? new Date(filterStartDate + 'T00:00:00Z')
      : new Date('2025-01-01T00:00:00Z');
    filtered = filtered.filter(r => new Date(r.start_time) >= chartStart);

    if (filterEndDate) {
      const endUTC = new Date(filterEndDate + 'T23:59:59Z');
      filtered = filtered.filter(r => new Date(r.start_time) <= endUTC);
    }

    // Separate Global records (these don't have region/country/city)
    const globalRecs = filtered.filter(r => r.name === 'Global');
    const regularRecs = filtered.filter(r => r.name !== 'Global');

    // Apply location filters only to regular records
    let locationFiltered = regularRecs;
    if (selectedRegions.length > 0) locationFiltered = locationFiltered.filter(r => selectedRegions.includes(r.warehouse_region));
    if (selectedCountries.length > 0) locationFiltered = locationFiltered.filter(r => selectedCountries.includes(r.warehouse_country));
    if (selectedWarehouses.length > 0) locationFiltered = locationFiltered.filter(r => selectedWarehouses.includes(r.name));

    return { filteredData: locationFiltered, globalRecords: globalRecs };
  }, [data, filterStartDate, filterEndDate, selectedRegions, selectedCountries, selectedWarehouses]);

  // ─── Period helpers ────────────────────────────────────────────────────────
  const getPeriodString = (date: Date): string => {
    if (periodType === 'yearly') return String(date.getUTCFullYear());
    if (periodType === 'monthly') return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
    if (periodType === 'quarterly') {
      const quarter = Math.floor(date.getUTCMonth() / 3) + 1;
      return `${date.getUTCFullYear()}-Q${quarter}`;
    }
    // weekly
    const year = getISOWeekYear(date);
    const week = getISOWeekNumber(date);
    return `${year}-W${String(week).padStart(2, '0')}`;
  };

  const formatPeriod = (period: string): string => {
    if (periodType === 'yearly') return period;
    if (periodType === 'quarterly') {
      const [year, quarter] = period.split('-Q');
      return `${year} Q${quarter}`;
    }
    if (periodType === 'monthly') {
      const [year, month] = period.split('-');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[parseInt(month) - 1]} ${year}`;
    }
    // weekly
    const [year, wk] = period.split('-W');
    return `${year} W${wk}`;
  };

  // ─── Get Previous Year Period ────────────────────────────────────────────
  const getPreviousYearPeriod = (period: string): string | null => {
    if (periodType === 'yearly') {
      const year = parseInt(period);
      return String(year - 1);
    }
    if (periodType === 'quarterly') {
      const [year, quarter] = period.split('-Q');
      return `${parseInt(year) - 1}-Q${quarter}`;
    }
    if (periodType === 'monthly') {
      const [year, month] = period.split('-');
      return `${parseInt(year) - 1}-${month}`;
    }
    // weekly
    const [year, wk] = period.split('-W');
    return `${parseInt(year) - 1}-W${wk}`;
  };

  // ─── Check if period is within data scope (>= 2025-W1) ────────────────────
  const isPeriodInDataScope = (period: string): boolean => {
    if (periodType === 'yearly') {
      return parseInt(period) >= 2025;
    }
    if (periodType === 'quarterly') {
      const [year, quarter] = period.split('-Q');
      return parseInt(year) >= 2025;
    }
    if (periodType === 'monthly') {
      const [year, month] = period.split('-');
      return parseInt(year) >= 2025;
    }
    // weekly
    const [year, wk] = period.split('-W');
    return parseInt(year) >= 2025;
  };

  const periodsToDisplay = useMemo(() => {
    // Use the active filter start date; fall back to 2025-W1 (report scope floor) when unset
    const start = filterStartDate
      ? new Date(filterStartDate + 'T00:00:00Z')
      : new Date('2025-01-01T00:00:00Z');
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    const end = filterEndDate ? new Date(filterEndDate + 'T23:59:59Z') : todayUTC;
    const current = new Date(start);
    const periods: string[] = [];

    while (current <= end) {
      periods.push(getPeriodString(current));

      if (periodType === 'yearly') {
        current.setUTCFullYear(current.getUTCFullYear() + 1);
      } else if (periodType === 'quarterly') {
        current.setUTCMonth(current.getUTCMonth() + 3);
      } else if (periodType === 'monthly') {
        current.setUTCMonth(current.getUTCMonth() + 1);
      } else {
        current.setUTCDate(current.getUTCDate() + 7);
      }
    }

    return Array.from(new Set(periods)).sort();
  }, [filterStartDate, filterEndDate, periodType]);

  const getTotalSecondsInPeriod = (period: string): number => {
    if (periodType === 'yearly') {
      const year = parseInt(period);
      const isLeap = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
      return (isLeap ? 366 : 365) * 86400;
    }
    if (periodType === 'quarterly') {
      const [year, quarter] = period.split('-Q').map(Number);
      // Calculate the number of days in the quarter
      const startMonth = (quarter - 1) * 3;
      const endMonth = startMonth + 3;
      let days = 0;
      for (let month = startMonth; month < endMonth; month++) {
        days += new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
      }
      return days * 86400;
    }
    if (periodType === 'monthly') {
      const [year, month] = period.split('-').map(Number);
      return new Date(Date.UTC(year, month, 0)).getUTCDate() * 86400;
    }
    // weekly
    return 7 * 86400;
  };

  // ─── Build hierarchy ──────────────────────────────────────────────────────
  const hierarchyData = useMemo(() => {
    // Build a complete structure map from warehouse metadata API
    const allWarehousesMap = new Map<string, { region: string; country: string; name: string }>();
    warehousesData.forEach(warehouse => {
      const key = warehouse.name;
      if (!allWarehousesMap.has(key)) {
        allWarehousesMap.set(key, {
          region: warehouse.region_name,
          country: warehouse.country,
          name: warehouse.name,
        });
      }
    });

    // Apply location filters to the complete warehouse list
    const filteredWarehouses = Array.from(allWarehousesMap.values()).filter(wh => {
      if (selectedRegions.length > 0 && !selectedRegions.includes(wh.region)) return false;
      if (selectedCountries.length > 0 && !selectedCountries.includes(wh.country)) return false;
      if (selectedWarehouses.length > 0 && !selectedWarehouses.includes(wh.name)) return false;
      
      // Apply critical filter
      if (showCriticalOnly) {
        const warehouseMeta = warehousesData.find(w => w.name === wh.name);
        if (!warehouseMeta || !warehouseMeta.is_critical) return false;
      }
      
      // Apply location status filter
      if (selectedLocationStatus.length > 0) {
        const warehouseMeta = warehousesData.find(w => w.name === wh.name);
        if (!warehouseMeta || !selectedLocationStatus.includes(warehouseMeta.location_status)) return false;
      }
      
      return true;
    });

    const groupByLevel = (warehouses: Array<{ region: string; country: string; name: string }>, level: 'region' | 'country' | 'warehouse'): Map<string, Array<{ region: string; country: string; name: string }>> => {
      const groups = new Map<string, Array<{ region: string; country: string; name: string }>>();
      warehouses.forEach(wh => {
        const key = level === 'region' ? wh.region : level === 'country' ? wh.country : wh.name;
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key)!.push(wh);
      });
      return groups;
    };

    const calculateMetrics = (warehouses: Array<{ region: string; country: string; name: string }>): MetricsByPeriod => {
      const metrics: MetricsByPeriod = {};

      // Get filtered records for these warehouses
      const warehouseNames = new Set(warehouses.map(w => w.name));
      const relevantRecords = filteredData.filter(r => warehouseNames.has(r.name));
      
      // Get all records (not just filtered) for previous year data calculation
      const allRelevantRecords = data.filter(r => warehouseNames.has(r.name));

      // Count unique warehouses in this group
      const warehouseCount = warehouses.length;

      periodsToDisplay.forEach(period => {
        metrics[period] = {
          incidentCount: 0,
          totalOutageSeconds: 0,
          totalAvailableSeconds: warehouseCount * getTotalSecondsInPeriod(period),
          previousYearIncidentCount: undefined,
        };
      });

      // Group records by period, warehouse, and inc_id to handle duplicates at site level
      const incidentsByPeriod = new Map<string, Map<string, OutageRecord[]>>();
      relevantRecords.forEach(record => {
        const period = getPeriodString(new Date(record.date));
        if (metrics[period]) {
          if (!incidentsByPeriod.has(period)) {
            incidentsByPeriod.set(period, new Map());
          }
          const periodIncidents = incidentsByPeriod.get(period)!;
          // Key by warehouse+inc_id for site-level deduplication
          const key = `${record.name}|${record.inc_id}`;
          if (!periodIncidents.has(key)) {
            periodIncidents.set(key, []);
          }
          periodIncidents.get(key)!.push(record);
        }
      });
      
      // For each period, warehouse, and incident, merge overlapping intervals and calculate duration
      incidentsByPeriod.forEach((incidents, period) => {
        if (metrics[period]) {
          // Count unique inc_ids (not warehouse+inc_id combinations)
          const uniqueIncIds = new Set<string>();
          
          incidents.forEach((incidentRecords, key) => {
            // Extract inc_id from the key (warehouse|inc_id)
            const inc_id = key.split('|')[1];
            uniqueIncIds.add(inc_id);
            
            // Sort records by start time
            const sortedRecords = incidentRecords.sort((a, b) => 
              new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
            );
            
            // Merge overlapping intervals
            const mergedIntervals: Array<{start: number, end: number}> = [];
            for (const record of sortedRecords) {
              const start = new Date(record.start_time).getTime();
              const end = new Date(record.end_time).getTime();
              
              if (mergedIntervals.length === 0 || mergedIntervals[mergedIntervals.length - 1].end < start) {
                // No overlap, add new interval
                mergedIntervals.push({ start, end });
              } else {
                // Overlap, merge by extending the end time if needed
                mergedIntervals[mergedIntervals.length - 1].end = Math.max(
                  mergedIntervals[mergedIntervals.length - 1].end,
                  end
                );
              }
            }
            
            // Calculate total duration from merged intervals
            const durationSeconds = mergedIntervals.reduce(
              (sum, interval) => sum + (interval.end - interval.start) / 1000,
              0
            );
            
            metrics[period].totalOutageSeconds += durationSeconds;
          });
          
          metrics[period].incidentCount = uniqueIncIds.size; // Count unique incidents
        }
      });
      
      // Calculate previous year incident counts for P1P2 targets
      periodsToDisplay.forEach(period => {
        const prevYearPeriod = getPreviousYearPeriod(period);
        if (prevYearPeriod && isPeriodInDataScope(prevYearPeriod)) {
          const prevYearRecords = allRelevantRecords.filter(record => {
            const recordPeriod = getPeriodString(new Date(record.date));
            return recordPeriod === prevYearPeriod;
          });
          
          // Count unique incidents in previous year period
          const uniquePrevYearIncidents = new Set<string>();
          prevYearRecords.forEach(record => {
            uniquePrevYearIncidents.add(record.inc_id);
          });
          
          metrics[period].previousYearIncidentCount = uniquePrevYearIncidents.size;
        }
      });
      
      return metrics;
    };

    const levelOrder: Array<'region' | 'country' | 'warehouse'> = ['region', 'country', 'warehouse'];

    const buildHierarchy = (warehouses: Array<{ region: string; country: string; name: string }>, currentLevel: 'region' | 'country' | 'warehouse'): HierarchyNode[] => {
      const nodes: HierarchyNode[] = [];
      const groups = groupByLevel(warehouses, currentLevel);
      groups.forEach((groupWarehouses, name) => {
        // Get all records for these warehouses (used for storage in node)
        const warehouseNames = new Set(groupWarehouses.map(w => w.name));
        const groupRecords = filteredData.filter(r => warehouseNames.has(r.name));
        
        const node: HierarchyNode = { 
          name, 
          level: currentLevel, 
          metrics: calculateMetrics(groupWarehouses), 
          records: groupRecords 
        };
        const currentIdx = levelOrder.indexOf(currentLevel);
        if (currentIdx < levelOrder.length - 1) {
          node.children = buildHierarchy(groupWarehouses, levelOrder[currentIdx + 1]);
        }
        nodes.push(node);
      });
      return nodes.sort((a, b) => a.name.localeCompare(b.name));
    };

    const startLevel: 'region' | 'country' | 'warehouse' =
      viewBy === 'Region' ? 'region' : viewBy === 'Country' ? 'country' : 'warehouse';

    let hierarchyNodes = buildHierarchy(filteredWarehouses, startLevel);
    
    // Apply "Only Red" filter
    if (showRedOnly) {
      const filterRedNodes = (nodes: HierarchyNode[]): HierarchyNode[] => {
        return nodes.filter(node => {
          let hasRedPeriod = false;
          
          if (activeTab === 'availability') {
            // Check if any period has availability < 99.5%
            hasRedPeriod = periodsToDisplay.some(period => {
              const m = node.metrics[period];
              if (!m) return false;
              const availability = m.totalAvailableSeconds === 0 ? 100 : 
                ((m.totalAvailableSeconds - m.totalOutageSeconds) / m.totalAvailableSeconds) * 100;
              return availability < 99.5;
            });
          } else if (activeTab === 'p1p2') {
            // Check if any period has P1P2 incidents exceeding target (target met = green)
            hasRedPeriod = periodsToDisplay.some(period => {
              const m = node.metrics[period];
              if (!m) return false;
              
              const actual = m.incidentCount;
              const prevYearCount = m.previousYearIncidentCount;
              
              // Skip if no previous year data (would show N/A)
              if (prevYearCount === undefined) return false;
              
              const target = Math.round(prevYearCount * 0.3);
              return actual > target; // Red when target not met
            });
          }
          
          if (hasRedPeriod) {
            // If node has children, recursively filter them too
            if (node.children) {
              node.children = filterRedNodes(node.children);
            }
            return true;
          }
          
          // If node doesn't have red periods but has children, check children
          if (node.children) {
            const filteredChildren = filterRedNodes(node.children);
            if (filteredChildren.length > 0) {
              node.children = filteredChildren;
              return true;
            }
          }
          
          return false;
        });
      };
      
      hierarchyNodes = filterRedNodes(hierarchyNodes);
    }
    
    // Add Global node if there are global records
    if (globalRecords.length > 0) {
      const globalMetrics: MetricsByPeriod = {};
      
      // Initialize all periods
      periodsToDisplay.forEach(period => {
        globalMetrics[period] = {
          incidentCount: 0,
          totalOutageSeconds: 0,
          totalAvailableSeconds: getTotalSecondsInPeriod(period), // Global is treated as a single entity
        };
      });
      
      // Group global records by period and inc_id to handle duplicates
      const incidentsByPeriod = new Map<string, Map<string, OutageRecord[]>>();
      globalRecords.forEach(record => {
        const period = getPeriodString(new Date(record.date));
        if (globalMetrics[period]) {
          if (!incidentsByPeriod.has(period)) {
            incidentsByPeriod.set(period, new Map());
          }
          const periodIncidents = incidentsByPeriod.get(period)!;
          const key = record.inc_id;
          if (!periodIncidents.has(key)) {
            periodIncidents.set(key, []);
          }
          periodIncidents.get(key)!.push(record);
        }
      });
      
      // Calculate metrics for each period
      incidentsByPeriod.forEach((incidents, period) => {
        if (globalMetrics[period]) {
          const uniqueIncIds = new Set<string>();
          
          incidents.forEach((incidentRecords, inc_id) => {
            uniqueIncIds.add(inc_id);
            
            // Sort records by start time
            const sortedRecords = incidentRecords.sort((a, b) => 
              new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
            );
            
            // Merge overlapping intervals
            const mergedIntervals: Array<{start: number, end: number}> = [];
            for (const record of sortedRecords) {
              const start = new Date(record.start_time).getTime();
              const end = new Date(record.end_time).getTime();
              
              if (mergedIntervals.length === 0 || mergedIntervals[mergedIntervals.length - 1].end < start) {
                mergedIntervals.push({ start, end });
              } else {
                mergedIntervals[mergedIntervals.length - 1].end = Math.max(
                  mergedIntervals[mergedIntervals.length - 1].end,
                  end
                );
              }
            }
            
            // Calculate total duration from merged intervals
            const durationSeconds = mergedIntervals.reduce(
              (sum, interval) => sum + (interval.end - interval.start) / 1000,
              0
            );
            
            globalMetrics[period].totalOutageSeconds += durationSeconds;
          });
          
          globalMetrics[period].incidentCount = uniqueIncIds.size;
        }
      });
      
      // Calculate previous year incident counts for Global node
      periodsToDisplay.forEach(period => {
        const prevYearPeriod = getPreviousYearPeriod(period);
        if (prevYearPeriod && isPeriodInDataScope(prevYearPeriod)) {
          const prevYearGlobalRecords = data.filter(record => {
            const recordPeriod = getPeriodString(new Date(record.date));
            // Global records are those without a name or with specific global identifiers
            const isGlobal = !record.name || record.name === '' || record.name === 'Global';
            return recordPeriod === prevYearPeriod && isGlobal;
          });
          
          const uniquePrevYearIncidents = new Set<string>();
          prevYearGlobalRecords.forEach(record => {
            uniquePrevYearIncidents.add(record.inc_id);
          });
          
          globalMetrics[period].previousYearIncidentCount = uniquePrevYearIncidents.size;
        }
      });
      
      const globalNode: HierarchyNode = {
        name: 'Global',
        level: viewBy === 'Region' ? 'region' : viewBy === 'Country' ? 'country' : 'warehouse',
        metrics: globalMetrics,
        records: globalRecords,
      };
      
      // Only include Global node if filters allow it
      // Exclude Global only when critical filter is active (since we can't determine if Global is critical)
      // Location status filter doesn't affect Global since it has no location
      if (!showCriticalOnly) {
        // Apply red filter to Global node if needed
        if (showRedOnly) {
          let hasRedPeriod = false;
          
          if (activeTab === 'availability') {
            hasRedPeriod = periodsToDisplay.some(period => {
              const m = globalNode.metrics[period];
              if (!m) return false;
              const availability = m.totalAvailableSeconds === 0 ? 100 : 
                ((m.totalAvailableSeconds - m.totalOutageSeconds) / m.totalAvailableSeconds) * 100;
              return availability < 99.5;
            });
          } else if (activeTab === 'p1p2') {
            hasRedPeriod = periodsToDisplay.some(period => {
              const m = globalNode.metrics[period];
              if (!m) return false;
              
              const actual = m.incidentCount;
              const prevYearCount = m.previousYearIncidentCount;
              
              if (prevYearCount === undefined) return false;
              
              const target = Math.round(prevYearCount * 0.3);
              return actual > target;
            });
          }
          
          if (hasRedPeriod) {
            // Insert Global node at the top
            hierarchyNodes = [globalNode, ...hierarchyNodes];
          }
        } else {
          // Insert Global node at the top
          hierarchyNodes = [globalNode, ...hierarchyNodes];
        }
      }
    }
    
    return hierarchyNodes;
  }, [warehousesData, filteredData, globalRecords, viewBy, periodsToDisplay, periodType, selectedRegions, selectedCountries, selectedWarehouses, showCriticalOnly, selectedLocationStatus, showRedOnly, activeTab, data]);

  // ─── Paginated Data ────────────────────────────────────────────────────────
  const paginatedHierarchyData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return hierarchyData.slice(startIndex, endIndex);
  }, [hierarchyData, page, rowsPerPage]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [selectedRegions, selectedCountries, selectedWarehouses, showCriticalOnly, selectedLocationStatus, showRedOnly, viewBy]);

  // ─── Totals ────────────────────────────────────────────────────────────────
  const calculateAvailability = (outageSeconds: number, availableSeconds: number): number => {
    if (availableSeconds === 0) return 100;
    return ((availableSeconds - outageSeconds) / availableSeconds) * 100;
  };

  const getAvailabilityColor = (availability: number): string => {
    if (availability >= 99.5) return '#4caf50';
    return '#f44336';
  };

  const getP1P2Color = (actual: number, target: number | string): string => {
    if (target === 'N/A') return 'white'; // white background when no target data
    if (actual <= (target as number)) return '#4caf50'; // green - target met
    return '#f44336'; // red - target not met
  };

  const calculateRowTotal = (node: HierarchyNode) => {
    let totalOutageSeconds = 0, totalAvailableSeconds = 0;
    periodsToDisplay.forEach(period => {
      const m = node.metrics[period];
      if (m) { totalOutageSeconds += m.totalOutageSeconds; totalAvailableSeconds += m.totalAvailableSeconds; }
    });
    
    // Count unique inc_ids across all periods for this node
    const uniqueIncidents = new Set<string>();
    node.records.forEach(record => {
      const period = getPeriodString(new Date(record.date));
      if (periodsToDisplay.includes(period)) {
        uniqueIncidents.add(record.inc_id);
      }
    });
    
    return { incidentCount: uniqueIncidents.size, totalOutageSeconds, totalAvailableSeconds };
  };

  const totals = useMemo(() => {
    const periodTotals: MetricsByPeriod = {};
    periodsToDisplay.forEach(p => { periodTotals[p] = { incidentCount: 0, totalOutageSeconds: 0, totalAvailableSeconds: 0, previousYearIncidentCount: undefined }; });
    
    // Sum outage seconds and available seconds from top-level nodes
    hierarchyData.forEach(node => {
      periodsToDisplay.forEach(p => {
        const m = node.metrics[p];
        if (m) {
          periodTotals[p].totalOutageSeconds += m.totalOutageSeconds;
          periodTotals[p].totalAvailableSeconds += m.totalAvailableSeconds;
        }
      });
    });
    
    // Count unique inc_ids per period across all filtered data (including global records)
    periodsToDisplay.forEach(period => {
      const uniqueIncidents = new Set<string>();
      // Include both regular filtered data and global records
      [...filteredData, ...globalRecords].forEach(record => {
        if (getPeriodString(new Date(record.date)) === period) {
          uniqueIncidents.add(record.inc_id);
        }
      });
      periodTotals[period].incidentCount = uniqueIncidents.size;
      
      // Calculate previous year incident count for totals
      // Only count incidents from the same warehouses that are in the current filtered view (including global)
      const prevYearPeriod = getPreviousYearPeriod(period);
      if (prevYearPeriod && isPeriodInDataScope(prevYearPeriod)) {
        // Get the set of warehouse names from the current filtered data (including global)
        const filteredWarehouseNames = new Set([...filteredData, ...globalRecords].map(r => r.name));
        
        const prevYearRecords = data.filter(record => {
          const recordPeriod = getPeriodString(new Date(record.date));
          // Only include records from warehouses that are in the current filtered view
          return recordPeriod === prevYearPeriod && filteredWarehouseNames.has(record.name);
        });
        
        const uniquePrevYearIncidents = new Set<string>();
        prevYearRecords.forEach(record => {
          uniquePrevYearIncidents.add(record.inc_id);
        });
        
        periodTotals[period].previousYearIncidentCount = uniquePrevYearIncidents.size;
      }
    });
    
    // Calculate totals
    let totalOutageSeconds = 0, totalAvailableSeconds = 0;
    Object.values(periodTotals).forEach(pt => {
      totalOutageSeconds += pt.totalOutageSeconds;
      totalAvailableSeconds += pt.totalAvailableSeconds;
    });
    
    // Count unique inc_ids across all periods in filtered data (including global records)
    const allUniqueIncidents = new Set<string>();
    [...filteredData, ...globalRecords].forEach(record => {
      const period = getPeriodString(new Date(record.date));
      if (periodsToDisplay.includes(period)) {
        allUniqueIncidents.add(record.inc_id);
      }
    });
    
    const grandTotal = { 
      incidentCount: allUniqueIncidents.size, 
      totalOutageSeconds, 
      totalAvailableSeconds 
    };
    
    return { periodTotals, grandTotal };
  }, [hierarchyData, periodsToDisplay, filteredData, globalRecords, data]);

  // ─── Toggle expansion ──────────────────────────────────────────────────────
  const toggleNode = (nodePath: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodePath)) newExpanded.delete(nodePath);
    else newExpanded.add(nodePath);
    setExpandedNodes(newExpanded);
  };

  // ─── Handle cell click to show details ─────────────────────────────────────
  const mergeOutageRecords = (records: OutageRecord[]): OutageRecord[] => {
    // Group by warehouse name + incident ID + outage number
    const groups = new Map<string, OutageRecord[]>();
    
    records.forEach(record => {
      const key = `${record.name}|${record.inc_id}|${record.outage_number}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(record);
    });

    // Merge each group
    const mergedRecords: OutageRecord[] = [];
    groups.forEach(groupRecords => {
      // Sort by start time
      groupRecords.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
      
      // Find earliest start and latest end
      const earliestStart = groupRecords[0].start_time;
      const latestEnd = groupRecords.reduce((latest, record) => {
        return new Date(record.end_time) > new Date(latest) ? record.end_time : latest;
      }, groupRecords[0].end_time);
      
      // Calculate total duration in seconds
      const durationSecs = (new Date(latestEnd).getTime() - new Date(earliestStart).getTime()) / 1000;
      
      // Create merged record using first record as template
      mergedRecords.push({
        ...groupRecords[0],
        start_time: earliestStart,
        end_time: latestEnd,
        outage_duration_secs: durationSecs,
      });
    });

    // Sort by start time
    return mergedRecords.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  };

  const handleCellClick = (node: HierarchyNode, period: string) => {
    // Filter records for this specific period
    const periodRecords = node.records.filter(record => {
      const recordPeriod = getPeriodString(new Date(record.date));
      return recordPeriod === period;
    });

    // Merge records with the same incident, warehouse, and outage number
    const mergedRecords = mergeOutageRecords(periodRecords);

    setDialogData({
      locationName: node.name,
      period: formatPeriod(period),
      records: mergedRecords,
      totalAvailableSeconds: node.metrics[period]?.totalAvailableSeconds,
    });
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogData(null);
  };

  const handleTotalCellClick = (period: string) => {
    const periodRecords = filteredData.filter(record => {
      const recordPeriod = getPeriodString(new Date(record.date));
      return recordPeriod === period;
    });

    const mergedRecords = mergeOutageRecords(periodRecords);

    setDialogData({
      locationName: 'TOTAL',
      period: formatPeriod(period),
      records: mergedRecords,
      totalAvailableSeconds: totals.periodTotals[period]?.totalAvailableSeconds,
    });
    setDialogOpen(true);
  };

  const exportDialogToCSV = () => {
    if (!dialogData || dialogData.records.length === 0) return;

    // Group by incident ID and warehouse to calculate deduplicated hours
    const groupedByIncidentAndWarehouse = new Map<string, OutageRecord[]>();
    dialogData.records.forEach(record => {
      const key = `${record.inc_id}|${record.name}`;
      if (!groupedByIncidentAndWarehouse.has(key)) {
        groupedByIncidentAndWarehouse.set(key, []);
      }
      groupedByIncidentAndWarehouse.get(key)!.push(record);
    });

    const headers = ['Incident ID', 'Warehouse', 'Outage Number', 'Start Time', 'End Time', 'Outage (hrs)', 'Deduplicated Outage (hrs)'];
    const rows: string[][] = [];
    
    groupedByIncidentAndWarehouse.forEach((records, key) => {
      const [incId, warehouse] = key.split('|');
      
      // Calculate deduplicated hours for this incident+warehouse
      const sortedRecords = records.sort((a, b) => 
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
      
      const mergedIntervals: Array<{start: number, end: number}> = [];
      for (const record of sortedRecords) {
        const start = new Date(record.start_time).getTime();
        const end = new Date(record.end_time).getTime();
        
        if (mergedIntervals.length === 0 || mergedIntervals[mergedIntervals.length - 1].end < start) {
          mergedIntervals.push({ start, end });
        } else {
          mergedIntervals[mergedIntervals.length - 1].end = Math.max(
            mergedIntervals[mergedIntervals.length - 1].end,
            end
          );
        }
      }
      
      const deduplicatedHours = mergedIntervals.reduce(
        (sum, interval) => sum + (interval.end - interval.start) / 1000 / 3600,
        0
      ).toFixed(2);
      
      sortedRecords.forEach(record => {
        rows.push([
          incId,
          warehouse,
          record.outage_number,
          new Date(record.start_time).toLocaleString('en-US', { timeZone: 'UTC' }) + ' UTC',
          new Date(record.end_time).toLocaleString('en-US', { timeZone: 'UTC' }) + ' UTC',
          (record.outage_duration_secs / 3600).toFixed(2),
          deduplicatedHours
        ]);
      });
    });

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incident_details_${dialogData.locationName}_${dialogData.period}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ─── Render table rows ─────────────────────────────────────────────────────
  const renderHierarchyRows = (nodes: HierarchyNode[], depth: number = 0, parentPath: string = ''): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    nodes.forEach(node => {
      const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
      const isExpanded = expandedNodes.has(nodePath);
      const hasChildren = node.children && node.children.length > 0;
      const rowTotal = calculateRowTotal(node);
      const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : depth === 2 ? '#f5f5f5' : '#f0f0f0';
      const rowTotalAvail = calculateAvailability(rowTotal.totalOutageSeconds, rowTotal.totalAvailableSeconds);

      const locationCell = (
        <TableCell
          rowSpan={2}
          sx={{
            pl: depth * 2.5 + 1,
            borderRight: '1px solid #e0e0e0',
            borderBottom: '2px solid #b0bec5',
            position: 'sticky',
            left: 0,
            backgroundColor: bgColor,
            zIndex: 1,
            cursor: hasChildren ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
          onClick={() => hasChildren && toggleNode(nodePath)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasChildren && (isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />)}
            {!hasChildren && <Box sx={{ width: 16 }} />}
            <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 600 : 400, fontSize: '0.8rem' }}>
              {node.name}
            </Typography>
          </Box>
        </TableCell>
      );

      // ── Target row ──
      rows.push(
        <TableRow key={`${nodePath}-target`} sx={{ backgroundColor: bgColor }}>
          {locationCell}
          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1565c0', borderRight: '2px solid #9e9e9e', whiteSpace: 'nowrap', py: 1.2 }}>
            Target
          </TableCell>
          {periodsToDisplay.map(period => (
            <TableCell key={`${nodePath}-target-${period}`} align="center" sx={{ borderRight: '2px solid #b0bec5', minWidth: 80, fontWeight: 600, fontSize: '0.8rem', py: 1.2 }}>99.5%</TableCell>
          ))}
          <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, fontSize: '0.8rem', py: 1.2 }}>99.5%</TableCell>
        </TableRow>
      );

      // ── Actual row ──
      rows.push(
        <TableRow key={`${nodePath}-actual`} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', borderRight: '2px solid #9e9e9e', borderBottom: '2px solid #b0bec5', whiteSpace: 'nowrap', py: 1.2 }}>
            Actual
          </TableCell>
          {periodsToDisplay.map(period => {
            const m = node.metrics[period];
            const availability = calculateAvailability(m.totalOutageSeconds, m.totalAvailableSeconds);
            return (
              <TableCell
                key={`${nodePath}-actual-${period}`}
                align="center"
                sx={{
                  borderRight: '2px solid #b0bec5',
                  minWidth: 80,
                  backgroundColor: getAvailabilityColor(availability),
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  borderBottom: '2px solid #b0bec5',
                  '&:hover': { opacity: 0.8 },
                  py: 1.2,
                }}
                onClick={() => handleCellClick(node, period)}
              >
                {availability.toFixed(3)}%
              </TableCell>
            );
          })}
          <TableCell
            align="center"
            sx={{
              backgroundColor: getAvailabilityColor(rowTotalAvail),
              color: 'white',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderBottom: '2px solid #b0bec5',
              py: 1.2,
            }}
          >
            {rowTotalAvail.toFixed(3)}%
          </TableCell>
        </TableRow>
      );

      if (isExpanded && hasChildren) {
        rows.push(...renderHierarchyRows(node.children!, depth + 1, nodePath));
      }
    });
    return rows;
  };

  // ─── Render P1/P2 rows with Target/Actual (like availability) ─────────────
  const renderP1P2Rows = (nodes: HierarchyNode[], depth: number = 0, parentPath: string = ''): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    nodes.forEach(node => {
      const nodePath = parentPath ? `${parentPath}/${node.name}` : node.name;
      const isExpanded = expandedNodes.has(nodePath);
      const hasChildren = node.children && node.children.length > 0;
      const rowTotal = calculateRowTotal(node);
      const bgColor = depth === 0 ? 'white' : depth === 1 ? '#fafafa' : depth === 2 ? '#f5f5f5' : '#f0f0f0';

      const locationCell = (
        <TableCell
          rowSpan={2}
          sx={{
            pl: depth * 2.5 + 1,
            borderRight: '1px solid #e0e0e0',
            borderBottom: '2px solid #b0bec5',
            position: 'sticky',
            left: 0,
            backgroundColor: bgColor,
            zIndex: 1,
            cursor: hasChildren ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
            verticalAlign: 'middle',
          }}
          onClick={() => hasChildren && toggleNode(nodePath)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {hasChildren && (isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />)}
            {!hasChildren && <Box sx={{ width: 16 }} />}
            <Typography variant="body2" sx={{ fontWeight: depth === 0 ? 600 : 400, fontSize: '0.8rem' }}>
              {node.name}
            </Typography>
          </Box>
        </TableCell>
      );

      // ── Target row ──
      rows.push(
        <TableRow key={`${nodePath}-target`} sx={{ backgroundColor: bgColor }}>
          {locationCell}
          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1565c0', borderRight: '2px solid #9e9e9e', whiteSpace: 'nowrap', py: 1.2 }}>
            Target
          </TableCell>
          {periodsToDisplay.map(period => {
            const m = node.metrics[period];
            const prevYearCount = m?.previousYearIncidentCount;
            let targetDisplay = 'N/A';
            
            if (prevYearCount !== undefined) {
              const target = Math.round(prevYearCount * 0.3);
              targetDisplay = `≤${target}`;
            }
            
            return (
              <TableCell key={`${nodePath}-target-${period}`} align="center" sx={{ borderRight: '2px solid #b0bec5', minWidth: 80, fontWeight: 600, fontSize: '0.8rem', py: 1.2 }}>
                {targetDisplay}
              </TableCell>
            );
          })}
          <TableCell align="center" sx={{ backgroundColor: '#f5f5f5', fontWeight: 600, fontSize: '0.8rem', py: 1.2 }}>
            —
          </TableCell>
        </TableRow>
      );

      // ── Actual row ──
      rows.push(
        <TableRow key={`${nodePath}-actual`} sx={{ backgroundColor: bgColor, '&:hover': { backgroundColor: '#e8f4fd' } }}>
          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', borderRight: '2px solid #9e9e9e', borderBottom: '2px solid #b0bec5', whiteSpace: 'nowrap', py: 1.2 }}>
            Actual
          </TableCell>
          {periodsToDisplay.map(period => {
            const m = node.metrics[period];
            const actual = m.incidentCount;
            const prevYearCount = m?.previousYearIncidentCount;
            
            let target: number | string = 'N/A';
            if (prevYearCount !== undefined) {
              target = Math.round(prevYearCount * 0.3);
            }
            
            const cellColor = getP1P2Color(actual, target);
            const textColor = target === 'N/A' ? '#333' : 'white';
            
            return (
              <TableCell
                key={`${nodePath}-actual-${period}`}
                align="center"
                sx={{
                  borderRight: '2px solid #b0bec5',
                  minWidth: 80,
                  backgroundColor: cellColor,
                  color: textColor,
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  cursor: actual > 0 ? 'pointer' : 'default',
                  borderBottom: '2px solid #b0bec5',
                  '&:hover': actual > 0 ? { opacity: 0.8 } : {},
                  py: 1.2,
                }}
                onClick={() => actual > 0 && handleCellClick(node, period)}
              >
                {actual > 0 ? actual : '0'}
              </TableCell>
            );
          })}
          <TableCell
            align="center"
            sx={{
              backgroundColor: '#f5f5f5',
              fontWeight: 600,
              fontSize: '0.8rem',
              borderBottom: '2px solid #b0bec5',
              py: 1.2,
            }}
          >
            {rowTotal.incidentCount > 0 ? rowTotal.incidentCount : '0'}
          </TableCell>
        </TableRow>
      );

      if (isExpanded && hasChildren) {
        rows.push(...renderP1P2Rows(node.children!, depth + 1, nodePath));
      }
    });
    return rows;
  };

  // ─── Export CSV ────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const dateStr = new Date().toISOString().split('T')[0];

    if (activeTab === 'p1p2') {
      const headers = [
        'Location',
        'Row Type',
        ...periodsToDisplay.map(p => `${formatPeriod(p)}`),
        'Total',
      ];

      const flattenNodes = (nodes: HierarchyNode[], depth: number = 0): any[][] => {
        const rows: any[][] = [];
        nodes.forEach(node => {
          const rt = calculateRowTotal(node);
          
          // Target row
          const targetRow = [
            '  '.repeat(depth) + node.name,
            'Target',
            ...periodsToDisplay.map(period => {
              const m = node.metrics[period];
              const prevYearCount = m?.previousYearIncidentCount;
              if (prevYearCount !== undefined) {
                const target = Math.round(prevYearCount * 0.3);
                return `≤${target}`;
              }
              return 'N/A';
            }),
            '0',
          ];
          
          // Actual row
          const actualRow = [
            '  '.repeat(depth) + node.name,
            'Actual',
            ...periodsToDisplay.map(period => {
              const m = node.metrics[period];
              return m.incidentCount > 0 ? m.incidentCount : 0;
            }),
            rt.incidentCount > 0 ? rt.incidentCount : 0,
          ];
          
          rows.push(targetRow, actualRow);
          if (node.children) rows.push(...flattenNodes(node.children, depth + 1));
        });
        return rows;
      };

      const dataRows = flattenNodes(hierarchyData);
      
      // Total Target row
      const totalTargetRow = [
        'TOTAL',
        'Target',
        ...periodsToDisplay.map(period => {
          const pt = totals.periodTotals[period];
          const prevYearCount = pt?.previousYearIncidentCount;
          if (prevYearCount !== undefined) {
            const target = Math.round(prevYearCount * 0.3);
            return `≤${target}`;
          }
          return 'N/A';
        }),
        '0',
      ];
      
      // Total Actual row
      const totalActualRow = [
        'TOTAL',
        'Actual',
        ...periodsToDisplay.map(period => totals.periodTotals[period].incidentCount),
        totals.grandTotal.incidentCount,
      ];
      
      dataRows.push(totalTargetRow, totalActualRow);

      const csv = [headers, ...dataRows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouse_p1p2_${dateStr}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else {
      const headers = [
        'Location',
        ...periodsToDisplay.map(p => `${formatPeriod(p)} - Availability (%)`),
        'Total - Availability (%)',
      ];

      const flattenNodes = (nodes: HierarchyNode[], depth: number = 0): any[][] => {
        const rows: any[][] = [];
        nodes.forEach(node => {
          const rt = calculateRowTotal(node);
          const row = [
            '  '.repeat(depth) + node.name,
            ...periodsToDisplay.map(period => {
              const m = node.metrics[period];
              return calculateAvailability(m.totalOutageSeconds, m.totalAvailableSeconds).toFixed(3);
            }),
            calculateAvailability(rt.totalOutageSeconds, rt.totalAvailableSeconds).toFixed(3),
          ];
          rows.push(row);
          if (node.children) rows.push(...flattenNodes(node.children, depth + 1));
        });
        return rows;
      };

      const dataRows = flattenNodes(hierarchyData);
      const totalRow = [
        'TOTAL',
        ...periodsToDisplay.map(period => {
          const pt = totals.periodTotals[period];
          return calculateAvailability(pt.totalOutageSeconds, pt.totalAvailableSeconds).toFixed(3);
        }),
        calculateAvailability(totals.grandTotal.totalOutageSeconds, totals.grandTotal.totalAvailableSeconds).toFixed(3),
      ];
      dataRows.push(totalRow);

      const csv = [headers, ...dataRows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouse_availability_${dateStr}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <WarehouseIcon sx={{ fontSize: 32, color: '#0067b3' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#001a35' }}>
            {activeTab === 'availability' ? 'Warehouse Availability' : 'Warehouse P1/P2'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" size="small" startIcon={<FileDownload />} onClick={exportToCSV} disabled={loading || hierarchyData.length === 0} sx={{ textTransform: 'none' }}>
            Export CSV
          </Button>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading} sx={{ textTransform: 'none' }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Main Layout: Left Slicers + Right Content */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {/* ─── LEFT SLICER PANEL ─────────────────────────────────────── */}
        <Paper sx={{ width: 260, minWidth: 260, p: 2, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 16 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#001a35', mb: 1.5, fontSize: '0.85rem' }}>
            Filters
          </Typography>

          {/* Period Type */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>Period Type</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <Select
              value={periodType}
              onChange={(e) => setPeriodType(e.target.value as any)}
              sx={{
                fontSize: '0.8rem',
                height: 36,
                backgroundColor: '#ffffff',
              }}
            >
              <MenuItem value="weekly" sx={{ fontSize: '0.8rem' }}>Weekly</MenuItem>
              <MenuItem value="monthly" sx={{ fontSize: '0.8rem' }}>Monthly</MenuItem>
              <MenuItem value="quarterly" sx={{ fontSize: '0.8rem' }}>Quarterly</MenuItem>
              <MenuItem value="yearly" sx={{ fontSize: '0.8rem' }}>Yearly</MenuItem>
            </Select>
          </FormControl>

          {/* Time Range */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>Time Range</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <Select
              value={selectedTimeRange}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedTimeRange(value);
                if (value === 'custom') {
                  setShowCustomDatePicker(true);
                } else {
                  setShowCustomDatePicker(false);
                }
              }}
              sx={{
                fontSize: '0.8rem',
                height: 36,
                backgroundColor: '#ffffff',
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>All Time</MenuItem>
              <MenuItem value="7d" sx={{ fontSize: '0.8rem' }}>Last 7 Days</MenuItem>
              <MenuItem value="30d" sx={{ fontSize: '0.8rem' }}>Last 30 Days</MenuItem>
              <MenuItem value="90d" sx={{ fontSize: '0.8rem' }}>Last 90 Days</MenuItem>
              <MenuItem value="1y" sx={{ fontSize: '0.8rem' }}>Year to Date</MenuItem>
              <MenuItem value="custom" sx={{ fontSize: '0.8rem' }}>Custom Range</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Date Range Picker */}
          {showCustomDatePicker && (
            <>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                fullWidth
                value={customDateRange.start}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setCustomDateRange({ 
                    start: newStart, 
                    end: customDateRange.end && new Date(customDateRange.end) >= new Date(newStart) 
                      ? customDateRange.end 
                      : '' 
                  });
                }}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                inputProps={{ 
                  style: { fontSize: '0.8rem' },
                  min: dataDateRange.min,
                  max: dataDateRange.max
                }}
                sx={{ mb: 1 }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                InputLabelProps={{ shrink: true, sx: { fontSize: '0.8rem' } }}
                inputProps={{
                  style: { fontSize: '0.8rem' },
                  min: customDateRange.start || dataDateRange.min,
                  max: dataDateRange.max
                }}
                disabled={!customDateRange.start}
                sx={{ mb: 1.5 }}
              />
            </>
          )}

          <Divider sx={{ mb: 1.5 }} />

          {/* View By */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>View By</Typography>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <Select
              value={viewBy}
              onChange={(e) => { setViewBy(e.target.value as any); setExpandedNodes(new Set()); }}
              sx={{ fontSize: '0.8rem', height: 36 }}
            >
              <MenuItem value="Region">Region</MenuItem>
              <MenuItem value="Country">Country</MenuItem>
              <MenuItem value="Warehouse">Warehouse</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ mb: 1.5 }} />

          {/* Location Filters */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>Location Filters</Typography>
          <Box sx={{ mb: 1 }}>
            {makeSelectAllAutocomplete('Region', availableRegions, selectedRegions, setSelectedRegions, () => { setSelectedCountries([]); setSelectedWarehouses([]); })}
          </Box>
          <Box sx={{ mb: 1 }}>
            {makeSelectAllAutocomplete('Country', availableCountries, selectedCountries, setSelectedCountries, () => { setSelectedWarehouses([]); })}
          </Box>
          <Box sx={{ mb: 1 }}>
            {makeSelectAllAutocomplete('Warehouse', availableWarehouses, selectedWarehouses, setSelectedWarehouses)}
          </Box>

          <Divider sx={{ mb: 1.5 }} />
<div style={{ paddingLeft: 8, paddingRight: 8 }}>
  {/* Critical Filter */}
          <FormControlLabel
            control={
              <Switch
                checked={showCriticalOnly}
                onChange={(e) => setShowCriticalOnly(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="caption" sx={{ fontSize: '0.8rem' }}>Critical</Typography>}
            sx={{ mb: 1 }}
          />

  {/* Red Filter - Available for both tabs */}
          <FormControlLabel
            control={
              <Switch
                checked={showRedOnly}
                onChange={(e) => setShowRedOnly(e.target.checked)}
                size="small"
              />
            }
            label={<Typography variant="caption" sx={{ fontSize: '0.8rem' }}>Red</Typography>}
            sx={{ mb: 1}}
          />

</div>
        
          <Divider sx={{ mb: 1.5 }} />

          {/* Location Status Filter */}
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#666', mb: 0.5, display: 'block' }}>Location Status</Typography>
          <Box sx={{ mb: 1.5 }}>
            {makeSelectAllAutocomplete('Status', availableLocationStatuses, selectedLocationStatus, setSelectedLocationStatus)}
          </Box>

          <Divider sx={{ mb: 1.5 }} />




          <Button
            variant="outlined"
            size="small"
            fullWidth
            startIcon={<Clear />}
            onClick={clearAllFilters}
            sx={{ fontSize: '0.75rem', textTransform: 'none', mb: 2 }}
          >
            Reset All Filters
          </Button>


        </Paper>

        {/* ─── RIGHT CONTENT AREA ────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Tab switcher */}
          <Paper sx={{ mb: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_, val) => setActiveTab(val)}
              sx={{ borderBottom: '1px solid #e0e0e0' }}
            >
              <Tab label="Availability" value="availability" sx={{ textTransform: 'none', fontWeight: 600 }} />
              <Tab label="P1/P2" value="p1p2" sx={{ textTransform: 'none', fontWeight: 600 }} />
            </Tabs>
          </Paper>

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && data.length > 0 && (
            <>
              {/* Data Table */}
              {hierarchyData.length > 0 ? (
                activeTab === 'availability' ? (
                <Paper>
                  <TableContainer sx={{ maxHeight: 700 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              backgroundColor: '#001a35',
                              color: 'white',
                              borderRight: '1px solid rgba(255,255,255,0.2)',
                              position: 'sticky',
                              left: 0,
                              top: 0,
                              zIndex: 4,
                              minWidth: 200,
                              fontSize: '0.8rem',
                            }}
                          >
                            Location
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              backgroundColor: '#001a35',
                              color: 'white',
                              borderRight: '2px solid rgba(255,255,255,0.4)',
                              top: 0,
                              zIndex: 2,
                              minWidth: 65,
                              fontSize: '0.8rem',
                            }}
                          />
                          {periodsToDisplay.map(period => (
                            <TableCell
                              key={period}
                              align="center"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: '#0067b3',
                                color: 'white',
                                borderRight: '2px solid rgba(255,255,255,0.3)',
                                fontSize: '0.8rem',
                                top: 0,
                                zIndex: 2,
                                minWidth: 80,
                              }}
                            >
                              {formatPeriod(period)}
                            </TableCell>
                          ))}
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, backgroundColor: '#001a35', color: 'white', fontSize: '0.8rem', top: 0, zIndex: 2 }}
                          >
                            TOTAL
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {renderHierarchyRows(paginatedHierarchyData)}

                        {/* Total Row — Target */}
                        <TableRow sx={{ backgroundColor: '#e3f2fd', borderTop: '3px solid #0067b3' }}>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.85rem', borderRight: '1px solid #e0e0e0', position: 'sticky', left: 0, backgroundColor: '#e3f2fd', zIndex: 1, verticalAlign: 'middle' }}>
                            TOTAL
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1565c0', borderRight: '2px solid #9e9e9e', whiteSpace: 'nowrap', py: 1.2 }}>Target</TableCell>
                          {periodsToDisplay.map(period => (
                            <TableCell key={`tot-target-${period}`} align="center" sx={{ fontWeight: 700, borderRight: '2px solid #b0bec5', backgroundColor: '#e3f2fd', fontSize: '0.8rem', py: 1.2 }}>99.5%</TableCell>
                          ))}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#bbdefb', fontSize: '0.8rem', py: 1.2 }}>99.5%</TableCell>
                        </TableRow>
                        {/* Total Row — Actual */}
                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', borderRight: '2px solid #9e9e9e', whiteSpace: 'nowrap', borderBottom: '2px solid #b0bec5', py: 1.2 }}>Actual</TableCell>
                          {periodsToDisplay.map(period => {
                            const pt = totals.periodTotals[period];
                            const avail = calculateAvailability(pt.totalOutageSeconds, pt.totalAvailableSeconds);
                            return (
                              <TableCell
                                key={`tot-actual-${period}`}
                                align="center"
                                sx={{ fontWeight: 700, borderRight: '2px solid #b0bec5', backgroundColor: getAvailabilityColor(avail), color: 'white', fontSize: '0.8rem', borderBottom: '2px solid #b0bec5', py: 1.2, cursor: 'pointer', '&:hover': { opacity: 0.8 } }}
                                onClick={() => handleTotalCellClick(period)}
                              >
                                {avail.toFixed(3)}%
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: getAvailabilityColor(calculateAvailability(totals.grandTotal.totalOutageSeconds, totals.grandTotal.totalAvailableSeconds)), color: 'white', fontSize: '0.8rem', borderBottom: '2px solid #b0bec5', py: 1.2 }}>
                            {calculateAvailability(totals.grandTotal.totalOutageSeconds, totals.grandTotal.totalAvailableSeconds).toFixed(3)}%
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <TablePagination
                    rowsPerPageOptions={[25, 50, 100, 200]}
                    component="div"
                    count={hierarchyData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{ borderTop: '1px solid #e0e0e0' }}
                  />
                </Paper>
                ) : (
                /* ── P1/P2 tab table ── */
                <Paper>
                  <TableContainer sx={{ maxHeight: 700 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              backgroundColor: '#001a35',
                              color: 'white',
                              borderRight: '1px solid rgba(255,255,255,0.2)',
                              position: 'sticky',
                              left: 0,
                              top: 0,
                              zIndex: 4,
                              minWidth: 200,
                              fontSize: '0.8rem',
                            }}
                          >
                            Location
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              backgroundColor: '#001a35',
                              color: 'white',
                              borderRight: '2px solid rgba(255,255,255,0.4)',
                              top: 0,
                              zIndex: 2,
                              minWidth: 65,
                              fontSize: '0.8rem',
                            }}
                          />
                          {periodsToDisplay.map(period => (
                            <TableCell
                              key={period}
                              align="center"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: '#0067b3',
                                color: 'white',
                                borderRight: '2px solid rgba(255,255,255,0.3)',
                                fontSize: '0.8rem',
                                top: 0,
                                zIndex: 2,
                                minWidth: 60,
                              }}
                            >
                              {formatPeriod(period)}
                            </TableCell>
                          ))}
                          <TableCell
                            align="center"
                            sx={{ fontWeight: 700, backgroundColor: '#001a35', color: 'white', fontSize: '0.8rem', top: 0, zIndex: 2 }}
                          >
                            TOTAL
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {renderP1P2Rows(paginatedHierarchyData)}
                        
                        {/* Total Row — Target */}
                        <TableRow sx={{ backgroundColor: '#e3f2fd', borderTop: '3px solid #0067b3' }}>
                          <TableCell rowSpan={2} sx={{ fontWeight: 700, fontSize: '0.85rem', borderRight: '1px solid #e0e0e0', position: 'sticky', left: 0, backgroundColor: '#e3f2fd', zIndex: 1, verticalAlign: 'middle' }}>
                            TOTAL
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1565c0', borderRight: '2px solid #9e9e9e', whiteSpace: 'nowrap', py: 1.2 }}>Target</TableCell>
                          {periodsToDisplay.map(period => {
                            const pt = totals.periodTotals[period];
                            const prevYearCount = pt?.previousYearIncidentCount;
                            let targetDisplay = 'N/A';
                            
                            if (prevYearCount !== undefined) {
                              const target = Math.round(prevYearCount * 0.3);
                              targetDisplay = `≤${target}`;
                            }
                            
                            return (
                              <TableCell key={`tot-target-${period}`} align="center" sx={{ fontWeight: 700, borderRight: '2px solid #b0bec5', backgroundColor: '#e3f2fd', fontSize: '0.8rem', py: 1.2 }}>
                                {targetDisplay}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#bbdefb', fontSize: '0.8rem', py: 1.2 }}>
                            —
                          </TableCell>
                        </TableRow>
                        {/* Total Row — Actual */}
                        <TableRow sx={{ backgroundColor: '#e3f2fd' }}>
                          <TableCell sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#555', borderRight: '2px solid #9e9e9e', whiteSpace: 'nowrap', borderBottom: '2px solid #b0bec5', py: 1.2 }}>Actual</TableCell>
                          {periodsToDisplay.map(period => {
                            const pt = totals.periodTotals[period];
                            const actual = pt.incidentCount;
                            const prevYearCount = pt?.previousYearIncidentCount;
                            
                            let target: number | string = 'N/A';
                            if (prevYearCount !== undefined) {
                              target = Math.round(prevYearCount * 0.3);
                            }
                            
                            const cellColor = getP1P2Color(actual, target);
                            const textColor = target === 'N/A' ? '#333' : 'white';
                            
                            return (
                              <TableCell
                                key={`p1p2-tot-${period}`}
                                align="center"
                                sx={{ fontWeight: 700, borderRight: '2px solid #b0bec5', backgroundColor: cellColor, color: textColor, fontSize: '0.8rem', borderBottom: '2px solid #b0bec5', py: 1.2, cursor: actual > 0 ? 'pointer' : 'default', '&:hover': actual > 0 ? { opacity: 0.8 } : {} }}
                                onClick={() => actual > 0 && handleTotalCellClick(period)}
                              >
                                {actual > 0 ? actual : '0'}
                              </TableCell>
                            );
                          })}
                          <TableCell align="center" sx={{ fontWeight: 700, backgroundColor: '#bbdefb', fontSize: '0.8rem', borderBottom: '2px solid #b0bec5', py: 1.2 }}>
                            {totals.grandTotal.incidentCount > 0 ? totals.grandTotal.incidentCount : '0'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', alignItems: 'center', borderTop: '1px solid #e0e0e0' }}>
                   
                      <IconButton size="small" sx={{ ml: 1 }}>
                        <HelpOutline fontSize="small" sx={{ color: '#666' }} />
                      </IconButton>
                      <span style={{ fontSize: '0.75rem', color: '#666' }}>Target is calculated as ≤30% of previous year's P1/P2 incidents for the same period and location.</span>
                    <TablePagination
                      rowsPerPageOptions={[25, 50, 100, 200]}
                      component="div"
                      count={hierarchyData.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Paper>
                )
              ) : (
                <Paper sx={{ p: 5, textAlign: 'center' }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No data matches the selected filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filter criteria or reset filters
                  </Typography>
                </Paper>
              )}
            </>
          )}

          {!loading && data.length === 0 && (
            <Paper sx={{ p: 5, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>No data available</Typography>
              <Typography variant="body2" color="text.secondary">Click "Refresh" to load warehouse availability data</Typography>
            </Paper>
          )}
        </Box>
      </Box>

      {/* Incident Details Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleDialogClose}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: '#001a35', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Incident Details - {dialogData?.locationName} ({dialogData?.period})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>

            <IconButton onClick={handleDialogClose} sx={{ color: 'white' }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {dialogData && dialogData.records.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Incident ID</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Warehouse</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Outage Number</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Start Time</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>End Time</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Outage (hrs)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Deduplicated Outage (hrs)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    // First group by incident ID, then by warehouse within each incident
                    const groupedByIncident = new Map<string, Map<string, OutageRecord[]>>();
                    
                    dialogData.records.forEach(record => {
                      if (!groupedByIncident.has(record.inc_id)) {
                        groupedByIncident.set(record.inc_id, new Map());
                      }
                      const warehouseMap = groupedByIncident.get(record.inc_id)!;
                      if (!warehouseMap.has(record.name)) {
                        warehouseMap.set(record.name, []);
                      }
                      warehouseMap.get(record.name)!.push(record);
                    });

                    const rows: React.ReactNode[] = [];
                    
                    groupedByIncident.forEach((warehouseMap, incId) => {
                      let isFirstWarehouseForIncident = true;
                      let totalRowsForIncident = 0;
                      
                      // Calculate total rows for this incident (for rowSpan)
                      warehouseMap.forEach(warehouseRecords => {
                        totalRowsForIncident += warehouseRecords.length;
                      });
                      
                      warehouseMap.forEach((warehouseRecords, warehouseName) => {
                        // Sort records by start time
                        const sortedRecords = warehouseRecords.sort((a, b) => 
                          new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
                        );
                        
                        // Calculate deduplicated lost hours for this incident+warehouse
                        // Merge overlapping intervals
                        const mergedIntervals: Array<{start: number, end: number}> = [];
                        for (const record of sortedRecords) {
                          const start = new Date(record.start_time).getTime();
                          const end = new Date(record.end_time).getTime();
                          
                          if (mergedIntervals.length === 0 || mergedIntervals[mergedIntervals.length - 1].end < start) {
                            mergedIntervals.push({ start, end });
                          } else {
                            mergedIntervals[mergedIntervals.length - 1].end = Math.max(
                              mergedIntervals[mergedIntervals.length - 1].end,
                              end
                            );
                          }
                        }
                        
                        const deduplicatedHours = mergedIntervals.reduce(
                          (sum, interval) => sum + (interval.end - interval.start) / 1000 / 3600,
                          0
                        );
                        
                        sortedRecords.forEach((record, recordIndex) => {
                          rows.push(
                            <TableRow key={`${record.inc_id}-${record.id}`} sx={{ '&:hover': { backgroundColor: '#f9f9f9' } }}>
                              {/* Incident ID - merged for all records of this incident */}
                              {isFirstWarehouseForIncident && recordIndex === 0 ? (
                                <TableCell 
                                  rowSpan={totalRowsForIncident}
                                  sx={{ 
                                    fontWeight: 600, 
                                    fontSize: '0.8rem',
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    borderRight: '2px solid #e0e0e0',
                                    backgroundColor: '#fafafa'
                                  }}
                                >
                                  <Typography
                                    component="a"
                                    href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/incident.do%3Fsysparm_query%3Dnumber%3D${record.inc_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                      color: '#0067b3',
                                      textDecoration: 'none',
                                      fontWeight: 600,
                                      fontSize: '0.8rem',
                                      '&:hover': {
                                        textDecoration: 'underline',
                                        color: '#004a87'
                                      }
                                    }}
                                  >
                                    {record.inc_id}
                                  </Typography>
                                </TableCell>
                              ) : null}
                              
                              {/* Warehouse - merged for all records of this incident+warehouse */}
                              {recordIndex === 0 ? (
                                <TableCell 
                                  rowSpan={sortedRecords.length}
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    verticalAlign: 'middle',
                                    textAlign: 'center',
                                    fontWeight: 600,
                                    borderRight: '1px solid #e0e0e0',
                                  }}
                                >
                                  {warehouseName}
                                </TableCell>
                              ) : null}
                              
                              {/* Individual outage details */}
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                <Typography
                                  component="a"
                                  href={`https://maersk.service-now.com/now/nav/ui/classic/params/target/cmdb_ci_outage_list.do%3Fsysparm_query%3Dnumber%3D${record.outage_number}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{
                                    color: '#0067b3',
                                    textDecoration: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    '&:hover': { textDecoration: 'underline', color: '#004a87' },
                                  }}
                                >
                                  {record.outage_number}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {new Date(record.start_time).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.8rem' }}>
                                {new Date(record.end_time).toLocaleString('en-US', { timeZone: 'UTC' })} UTC
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.8rem' }}>
                                {(record.outage_duration_secs / 3600).toFixed(2)}
                              </TableCell>
                              
                              {/* Deduplicated Outage - merged for all records of this incident+warehouse */}
                              {recordIndex === 0 ? (
                                <TableCell 
                                  rowSpan={sortedRecords.length}
                                  align="right"
                                  sx={{ 
                                    fontSize: '0.8rem',
                                    verticalAlign: 'middle',
                                    fontWeight: 600,
                                  }}
                                >
                                  {deduplicatedHours.toFixed(2)}
                                </TableCell>
                              ) : null}
                            </TableRow>
                          );
                        });
                        
                        isFirstWarehouseForIncident = false;
                      });
                    });

                    return rows;
                  })()}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              No incident records available for this period.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: 70 }}>
          {/* Availability Calculation */}
          {dialogData && dialogData.records.length > 0 ? (() => {
            // Calculate total deduplicated outage hours
            const groupedByIncidentAndWarehouse = new Map<string, OutageRecord[]>();
            dialogData.records.forEach(record => {
              const key = `${record.inc_id}|${record.name}`;
              if (!groupedByIncidentAndWarehouse.has(key)) {
                groupedByIncidentAndWarehouse.set(key, []);
              }
              groupedByIncidentAndWarehouse.get(key)!.push(record);
            });

            let totalDeduplicatedHours = 0;
            groupedByIncidentAndWarehouse.forEach((records) => {
              const sortedRecords = records.sort((a, b) => 
                new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
              );
              
              const mergedIntervals: Array<{start: number, end: number}> = [];
              for (const record of sortedRecords) {
                const start = new Date(record.start_time).getTime();
                const end = new Date(record.end_time).getTime();
                
                if (mergedIntervals.length === 0 || mergedIntervals[mergedIntervals.length - 1].end < start) {
                  mergedIntervals.push({ start, end });
                } else {
                  mergedIntervals[mergedIntervals.length - 1].end = Math.max(
                    mergedIntervals[mergedIntervals.length - 1].end,
                    end
                  );
                }
              }
              
              const deduplicatedHours = mergedIntervals.reduce(
                (sum, interval) => sum + (interval.end - interval.start) / 1000 / 3600,
                0
              );
              
              totalDeduplicatedHours += deduplicatedHours;
            });

            // Use the total available seconds from the node metrics if available,
            // otherwise calculate from unique warehouses (for TOTAL row)
            let totalAvailableHours: number;
            if (dialogData.totalAvailableSeconds !== undefined) {
              totalAvailableHours = dialogData.totalAvailableSeconds / 3600;
            } else {
              const uniqueWarehouses = new Set(dialogData.records.map(r => r.name));
              const warehouseCount = uniqueWarehouses.size;
              const periodDurationSeconds = getTotalSecondsInPeriod(
                periodsToDisplay.find(p => formatPeriod(p) === dialogData.period) || periodsToDisplay[0]
              );
              totalAvailableHours = (warehouseCount * periodDurationSeconds) / 3600;
            }
            
            const availability = ((totalAvailableHours - totalDeduplicatedHours) / totalAvailableHours) * 100;

            return (
              <Box>
                <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                  <strong>Availability:</strong> (({totalAvailableHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs - {totalDeduplicatedHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs) / {totalAvailableHours.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs)  x 100 = <span style={{ color: getAvailabilityColor(availability), fontWeight: 700 }}>{availability.toFixed(3)}%</span>
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: '#999', fontStyle: 'italic', mt: 0.5 }}>
                  Formula: ((Available Hours - Deduplicated Outage Hours) / Available Hours) × 100
                </Typography>
              </Box>
            );
          })() : <Box />}
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              startIcon={<FileDownload />} 
              onClick={exportDialogToCSV}
              variant="contained"
            >
              Export
            </Button>
            <Button onClick={handleDialogClose} variant="contained">
              Close
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

  </Box>
  );
};

export default WarehouseAvailability;
